<script lang="ts">
  import { onMount } from "svelte";
  import type {
    IntegrationDetails,
    ExternalContacts,
    IntegrationChat,
    TrackingCategoryMapping,
    SupplyLineMapping,
  } from "../../helpers/types";
  import { track } from "../../helpers/mixpanel";
  import {
    setSetupStep,
    setupStepStore,
    mappableTrackingCategoriesStore,
  } from "../../stores/integrations-store";
  import { saveTrackingCategoryMapping } from "../../helpers/integrations-api";
  import StepSuccessIcon from "./StepSuccessIcon.svelte";
  import { selectedBuyer } from "../../stores/buyer";

  export let integration: IntegrationDetails;
  export let contacts: ExternalContacts | null;
  export let stepNumber: number = 0;
  export let isActive: boolean = false;
  export let authenticated: boolean = false;
  export let currentStep: number = 0;

  $: active = (stepNumber && authenticated && isActive) || (authenticated && !stepNumber);
  $: mappingsComplete = true;

  let mappedContacts: Record<string, SupplyLineMapping>;
  $: mappedContacts = {};
  let proChatsSortedByHasOrder: IntegrationChat[] = [];

  let trackingCategoryMappings: Record<string, TrackingCategoryMapping> = {};

  const getQualifyingSupplyLines = () => {
    const mappedChats = integration.chats?.filter(
      (chat) => mappedContacts[chat.chat_id]?.external_supplier_contact
    );
    return mappedChats.sort((a, b) => {
      if (a.order_id && !b.order_id) {
        return -1;
      }

      if (b.order_id && !a.order_id) {
        return 1;
      }

      return 0;
    });
  };

  mappableTrackingCategoriesStore.subscribe((value) => {
    mappedContacts = value;
    proChatsSortedByHasOrder = getQualifyingSupplyLines();
  });

  const selectTrackingCategory = (chat: IntegrationChat, e: any) => {
    trackingCategoryMappings[chat.chat_id].external_tc_category = e.target.value;
    trackingCategoryMappings[chat.chat_id].external_tc_option = "";

    track("integration_external_tracking_category_update", {
      buyer_id: $selectedBuyer?.id,
      buyer_name: $selectedBuyer?.name,
      integration_id: integration.id,
      platform: integration.platform,
      external_site_id: integration.site_id,
      category: trackingCategoryMappings[chat.chat_id].external_tc_category,
      type: stepNumber ? "setup" : "management",
    });
  };

  onMount(() => {
    proChatsSortedByHasOrder = getQualifyingSupplyLines();

    proChatsSortedByHasOrder.forEach((chat) => {
      const externalTrackingCat = JSON.parse(chat?.external_tracking_categories || "{}");

      trackingCategoryMappings[chat.chat_id] = {
        supplier_id: chat.supplier_id,
        external_tc_category: externalTrackingCat?.category || "",
        external_tc_option: externalTrackingCat?.option || "",
        integration_id: chat.integration_id,
      };

      if (!trackingCategoryMappings[chat.chat_id].external_tc_option) {
        mappingsComplete = false;
      }
    });
  });

  const updateTrackingCatOption = (chat: IntegrationChat, e: any) => {
    const option = e.target.value;
    trackingCategoryMappings[chat.chat_id].external_tc_option = option;

    track("integration_external_tracking_categories_update_option", {
      buyer_id: $selectedBuyer?.id,
      buyer_name: $selectedBuyer?.name,
      integration_id: integration.id,
      platform: integration.platform,
      external_site_id: integration.site_id,
      chat_id: chat.chat_id,
      chat_name: chat.chat_name,
      tracking_category: trackingCategoryMappings[chat.chat_id].external_tc_category,
      option,
      type: stepNumber ? "setup" : "management",
    });

    // Default to true and if any chats are not mapped set the flag to false otherwise leave it alone
    mappingsComplete = true;
    Object.values(trackingCategoryMappings).forEach((mapping) => {
      if (!mapping.external_tc_option) {
        mappingsComplete = false;
      }
    });

    saveTrackingCategoryMapping(trackingCategoryMappings[chat.chat_id]).then(() => {
      setSetupStep(integration, mappingsComplete);
    });
  };

  $: getOptionsList = (chat: IntegrationChat) => {
    // const selectedCat =
    //   selectedTrackingCategory ||
    //   trackingCategoryMappings[chat.chat_id]?.external_tc_category ||
    //   "";
    return (
      contacts?.tracking_categories[trackingCategoryMappings[chat.chat_id]?.external_tc_category]
        ?.options || []
    );
  };
</script>

<div class="container">
  <div class="supply-line-header">
    <StepSuccessIcon
      success={authenticated && mappingsComplete && !!proChatsSortedByHasOrder.length}
      {isActive}
      {stepNumber}
    />
    <h4 class="subtitle">tracking categories (optional)</h4>
    {#if authenticated && currentStep > stepNumber}
      <button class="contact-us-btn btn-neutral" on:click={() => setupStepStore.set(stepNumber)}
        >edit
      </button>
    {/if}
  </div>
  {#if active}
    <div class="content">
      {#if contacts?.tracking_categories}
        {#if proChatsSortedByHasOrder?.length}
          {#each proChatsSortedByHasOrder as chat}
            <div class="supply-line-row">
              {#if trackingCategoryMappings[chat.chat_id]?.external_tc_option}
                <img
                  class="supply_line_icon"
                  alt={`success-tick`}
                  src="static/assets/icons/small_tick.svg"
                />
              {:else}
                <div class="supply_line_dot" />
              {/if}
              <div class="chatname-container">
                <span class="supply-line-chatname">{chat.chat_name}</span>
                {#if !chat.order_id}
                  <span class="no-orders">no orders yet</span>
                {/if}
              </div>
              <div class="select-container">
                <select
                  on:change={(event) => selectTrackingCategory(chat, event)}
                  value={trackingCategoryMappings[chat.chat_id]?.external_tc_category || ""}
                >
                  <option value={""} selected>Select Category</option>
                  {#each Object.keys(contacts?.tracking_categories || "") as option}
                    <option value={option}>{option}</option>
                  {/each}
                </select>
                <select
                  on:change={(event) => updateTrackingCatOption(chat, event)}
                  value={trackingCategoryMappings[chat.chat_id]?.external_tc_option || ""}
                >
                  <option value={""} selected>Select Option</option>
                  {#each getOptionsList(chat) as option}
                    <option value={option}>{option}</option>
                  {/each}
                </select>
              </div>
            </div>
          {/each}
        {:else}
          <div class="empty-container">
            <p class="empty-text">
              Map your supply lines in rekki to your accounting contacts to add tracking
            </p>
          </div>
        {/if}
      {:else}
        <div class="empty-container">
          <p class="empty-text">
            start some pro chats in the app to get started with tracking categories
          </p>
        </div>
      {/if}
    </div>
  {:else}
    <div class="empty-container">
      <p class="empty-text">set up tracking categories in Xero to get started</p>
    </div>
  {/if}
</div>

<style>
  .container {
    border-radius: 8px;
    background-color: white;
    margin-bottom: 18px;
  }

  .supply-line-header {
    display: flex;
    padding: 18px;
    border-bottom: 1px solid;
    border-color: var(--rekki-neutral-01);
    align-items: center;
  }
  .content {
    padding: 18px;
  }

  .supply-line-row {
    display: flex;
    margin-bottom: 18px;
    align-items: center;
  }

  .supply-line-chatname {
    font-size: 18px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }

  .select-container {
    margin-left: auto;
    justify-content: flex-end;
    display: flex;
    flex: 1;
    max-width: 60%;
  }

  .supply_line_icon {
    height: 36px;
    width: 36px;
  }

  .supply_line_dot {
    border-radius: 50%;
    background-color: var(--rekki-warning);
    height: 8px;
    width: 8px;
    border: none;
    margin-right: 16px;
    margin-left: 12px;
    margin-top: 3px;
    min-width: 8px;
  }

  .chatname-container {
    display: flex;
    flex-direction: column;
    max-width: 33%;
  }

  .no-orders {
    font-size: 16px;
    color: var(--rekki-neutral-03);
  }
  select {
    border: none;
    background-color: var(--rekki-neutral-01);
    border-radius: 8px;
    font-size: 18px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;

    height: 60px;
    padding-left: 8px;
    padding-right: 8px;
    margin-left: 8px;
    min-width: 180px;
    flex: 1;
  }

  .subtitle {
    font-size: 18px;
    font-weight: bold;
  }
  .contact-us-btn {
    border-radius: 100px;
    padding: 10px 20px;
    background-color: var(--rekki-dark);
    color: var(--rekki-light);
    border: none;
    justify-self: flex-end;
    font-family: "REKKI-OCD";
    white-space: nowrap;
    text-transform: uppercase;
    margin-left: auto;
    font-size: 16px;
  }

  .btn-neutral {
    background-color: var(--rekki-neutral-01);
    color: var(--rekki-dark);
  }

  .empty-container {
    display: flex;
    padding: 20px 0;
  }

  .empty-text {
    margin: 0 auto;
  }
</style>
